import React, { forwardRef, memo, ReactNode } from 'react';
import { DOMProps } from '@react-types/shared';
import { AriaButtonProps, HoverProps, mergeProps, useButton, useHover } from 'react-aria';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';
import { isArrayOfStrings } from '@direct-frontend/stdlib';

import { TestProps } from '../../../internal/types/testTypes';
import { cn } from '../../../lib/css/className/className';
import { SkinColor, SkinRole } from '../../../lib/skin/getColorSkin/generated/types';
import './BaseFilterChip.css';
import { HStack } from '../../Stack';
import { Text } from '../../Text';
import { ClickableIcon } from '../../ClickableIcon';
import { IconCloseSkinny, IconErrorRed } from '../../../icons';
import { useObjectRef } from '../../../hooks/useObjectRef';
import { StatefulTooltip } from '../../StatefulTooltip';
import { getColorSkin } from '../../../lib/skin/getColorSkin/getColorSkin';
import { useFocusOptions } from '../../../hooks/useFocusOptions';

const cls = cn('BaseFilterChip');

export const skinMap: Record<FilterColor, SkinColor | SkinRole> = {
    normal: 'gray-low-contrast',
    contrast: 'gray-neutral',
    contour: 'gray-transparent-contour',
};

const textSizeMap = {
    xs: 's',
    s: 'mc',
    m: 'm',
} as const;

export type FilterColor = 'contour' | 'contrast' | 'normal';
export type FilterSize = 'xs' | 's' | 'm';
export type BaseFilterChipRole = 'key' | 'value' | 'system';

const iconSizeMap = {
    xs: '16',
    s: '16',
    m: '24',
} as const;
const badgeSizeMap = {
    xs: '16',
    s: '16',
    m: '16',
} as const;
const checkboxSizeMap = {
    xs: 'xs',
    s: 'xs',
    m: 'm',
} as const;

export const useFilterChipAddonSizes = (size: FilterSize) => {
    return {
        iconSize: iconSizeMap[size],
        badgeSize: badgeSizeMap[size],
        checkboxSize: checkboxSizeMap[size],
    };
};

export interface FilterCommonProps {
    /**
     * Цвет элемента
     */
    color: FilterColor;

    /**
     * Размер элемента
     */
    size: FilterSize;
}

export interface BaseFilterChipProps
    extends FilterCommonProps,
        Loggable,
        TestProps,
        DOMProps,
        Pick<HoverProps, 'onHoverStart' | 'onHoverEnd'>,
        Partial<AriaButtonProps<'div'>> {
    /**
     * Тип чипса. Чипс с ключем или со значением фильтра
     */
    role: BaseFilterChipRole;

    /**
     * Контент чипса. Если строка, то оборачивается в Text
     */
    children?: ReactNode;

    /**
     * Ошибка, отображается как иконка с тултипом – описанием ошибки
     */
    error?: ReactNode;

    /**
     * Слот слева от текста для чекбокса
     */
    addonLeft?: ReactNode;

    /**
     * Слот справа от текста для бейджей и иконок
     */
    addonRight?: ReactNode;

    /**
     * Колбэк нажатия на чипс. При указании чипс становится интерактивным, получает стили на hover
     */
    onClick?: () => void;

    /**
     * Интерактивен ли чипс. Если true, то чипс получает стили на hover
     * Если передан onClick или onPress, то чипс становится интерактивным по-умолчанию
     */
    isInteractive?: boolean;

    /**
     * Активен ли сейчас чипс. Может означать например, то, что он выбран
     */
    isActive?: boolean;

    /**
     * Задизейблен ли чипс
     */
    isDisabled?: boolean;

    /**
     * Колбэк нажатия на крестик. Крестик появляется при указании этого свойства
     */
    onRemove?: () => void;

    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Используется ли компонент, как блок, заполняющий пустое пространство
     */
    isFiller?: boolean;

    /**
     * Ширина чипса.
     * max — на всю ширину контейнера (для триггера FilterSelect с width="max").
     * @default auto
     */
    width?: 'auto' | 'max';

    /**
     * tabIndex для компонента
     */
    tabIndex?: number;
}

/**
 * Базовый компонент для отображения составной части фильтра – чипса
 */
export const BaseFilterChip = memo(
    forwardRef<HTMLDivElement, BaseFilterChipProps>(function BaseFilterChip(
        {
            size,
            color,
            role,
            children,
            error,
            addonLeft,
            addonRight,
            onClick,
            isInteractive,
            isDisabled,
            isActive,
            onRemove,
            className,
            'data-testid': dataTestId,
            isFiller,
            width = 'auto',
            logNode,
            tabIndex,
            ...props
        },
        forwardedRef,
    ) {
        const { LogProvider } = useLogger('BaseFilterChip', logNode);

        const ref = useObjectRef(forwardedRef);
        const { hoverProps, isHovered } = useHover({
            isDisabled,
            onHoverStart: props.onHoverStart,
            onHoverEnd: props.onHoverEnd,
        });
        const { buttonProps } = useButton({ onPress: onClick, elementType: 'div', isDisabled, ...props }, ref);
        // TODO DIRECT-263743 Поресерчить фокусы в модалке на мобиле, после этого если нужно - откатить
        const { focusProps, isFocusVisible } = useFocusOptions();
        const { iconSize } = useFilterChipAddonSizes(size);
        const hasClickHandler = Boolean(onClick || props.onPress);

        const hasRightAddons = Boolean(addonRight || onRemove);

        return (
            <LogProvider>
                <div
                    className={cls(
                        {
                            size,
                            color,
                            role,
                            width,
                            hovered: isHovered,
                            focused: isFocusVisible,
                            active: !isDisabled && isActive,
                            disabled: isDisabled,
                            filler: isFiller,
                        },
                        [getColorSkin(skinMap[color], { isHovered, isDisabled }), className],
                    )}
                    {...(hasClickHandler
                        ? mergeProps(hoverProps, buttonProps, focusProps, tabIndex ? { tabIndex } : {})
                        : {})}
                    {...(!hasClickHandler && isInteractive ? hoverProps : {})}
                    ref={ref}
                    data-testid={dataTestId}
                >
                    <HStack className={cls('content')} gap="8" alignItems="center">
                        {addonLeft}
                        {typeof children === 'string' || isArrayOfStrings(children) ? (
                            <Text
                                size={textSizeMap[size]}
                                color="current"
                                role={role === 'key' ? 'main' : 'supplementary'}
                                isTruncated
                            >
                                {children}
                            </Text>
                        ) : (
                            children
                        )}
                        {error && (
                            <StatefulTooltip title={error}>
                                <ClickableIcon>
                                    <IconErrorRed size={iconSize} />
                                </ClickableIcon>
                            </StatefulTooltip>
                        )}
                        {hasRightAddons ? (
                            <HStack className={cls('right')} gap="8" alignItems="center">
                                {addonRight}
                                {onRemove && (
                                    <ClickableIcon
                                        onClick={onRemove}
                                        isDisabled={isDisabled}
                                        color="current"
                                        data-testid={dataTestId && `${dataTestId}.Remove`}
                                        logNode={'BaseFilterChipRemoveButton'}
                                    >
                                        <IconCloseSkinny size={iconSize} />
                                    </ClickableIcon>
                                )}
                            </HStack>
                        ) : null}
                    </HStack>
                </div>
            </LogProvider>
        );
    }),
);
